import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Text } from '~/elements/NewText';
import { Button } from '~/elements/Button';

// Custom Text component with Suisse font
const SuisseText = styled(Text)`
  font-family: 'Suisse', sans-serif;
`;

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 90%;
  min-height: 400px;
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1);
`;

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  /* background-color: #eee; */
  overflow-y: auto;

  /* Hide scrollbar for all browsers */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */

  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
`;

const MessagesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 2rem;
`;

const MessagesContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const fadeIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0px);
  }
`;

const MessageRowWrapper = styled.div<{ isNew?: boolean; isConsecutive?: boolean }>`
  width: 100%;
  margin-bottom: 0.5rem;
  opacity: ${({ isNew }) => (isNew ? 0 : 1)};

  ${({ isNew }) =>
    isNew &&
    css`
      animation: ${fadeIn} 0.5s ease-out forwards;
    `}
`;

const MessageRow = styled.div<{ isUser: boolean }>`
  display: flex;
  justify-content: ${({ isUser }) => (isUser ? 'flex-end' : 'flex-start')};
  width: 100%;
`;

const MessageContainer = styled.div<{ isLastMessage: boolean; isUser?: boolean; isConsecutive?: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 0 16px;
  max-width: 80%;
  margin-bottom: ${(props) => (props.isLastMessage ? '10px' : '0')};
  margin-left: ${(props) => (props.isConsecutive ? '44px' : '0')};
  background-color: ${(props) => (props.isUser ? '#4575CD' : '#f0f0f0')};
  color: ${(props) => (props.isUser ? '#fff' : '#222')};
  box-shadow: ${(props) =>
    props.isUser ? 'inset 0 0 0 1px rgba(27, 45, 112, 0.5)' : 'inset 0 0 0 1px rgba(0, 0, 0, 0.05)'};
  border-radius: 12px;
  animation: ${fadeIn} 0.3s ease;
`;

const Avatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  margin-right: 0.75rem;
  object-fit: cover;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.1);
  background-color: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
`;

const MessageWithAvatar = styled.div`
  display: flex;
  align-items: flex-start;
`;

const OptionsContainer = styled.div`
  height: 80px;
  background-color: rgba(0, 0, 0, 0.025);
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.05);
  padding: 1.25rem 1.5rem;
  border-radius: 8px;
  display: flex;
  align-items: flex-start;
  margin: 0 12px 12px 12px;
`;

const OptionsButtonContainer = styled.div<{ isVisible: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  flex-wrap: wrap;
  width: 100%;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transition: opacity 0.3s ease;
`;

const OptionButton = styled(Button)`
  flex: 0 0 auto;
  min-width: 120px;
  padding-left: 24px;
  padding-right: 24px;
  justify-content: center;
  background-color: white;
  color: #333;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.05), 0 1px 1px rgba(0, 0, 0, 0.1);
  border: none;
  font-weight: 500;

  &:hover {
    background-color: white;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.15), 0 4px 8px rgba(0, 0, 0, 0.05);
  }
`;

const DownloadButtonWrapper = styled.div`
  margin-bottom: 1rem;
`;

const ImagePreviewContainer = styled.div`
  display: flex;
  margin-bottom: 16px;
`;

const WordmarkPreview = styled.img`
  width: 100%;
  max-width: 180px;
  height: auto;
  padding: 32px;
  border-radius: 8px;
  background-color: rgba(255, 255, 255, 0.5);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.5);
  aspect-ratio: 4.55 / 1;
`;

const SymbolPreview = styled.img`
  width: 100%;
  max-width: 100px;
  height: auto;
  padding: 32px;
  border-radius: 8px;
  background-color: rgba(255, 255, 255, 0.5);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.5);
  aspect-ratio: 1 / 1;
`;

type Message = {
  sender: 'ryanbot' | 'user';
  content: string;
  isDownload?: boolean;
  isNew?: boolean;
  showSymbol?: boolean;
  showWordmark?: boolean;
};

type Selection = {
  type: 'wordmark' | 'symbol' | null;
  color: 'black' | 'white' | 'blue' | null;
  format: 'svg' | 'png' | null;
};

const conversation = [
  {
    id: 'type',
    prompt: 'Yo np, do you need the wordmark or the symbol?',
    options: ['Wordmark', 'Symbol', 'What??'],
    response: (val: string) =>
      val === 'What??' ? 'Haha valid, ok let me explain —' : `Cool, ok the ${val.toLowerCase()}`,
  },
  {
    id: 'color',
    prompt: 'What color do you need?',
    options: ['Black', 'White', 'Blue'],
    response: (val: string) => 'Cool np',
  },
  {
    id: 'format',
    prompt: 'Last thing, need a .png or .svg?',
    options: ['.svg', '.png'],
    response: (val: string) => `Ok I'll grab you the ${val}`,
  },
];

export const Ryanbot: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      sender: 'user',
      content: 'Hey Ryan, where can I grab a Column logo?',
    },
  ]);
  const [currentStep, setCurrentStep] = useState(-1);
  const [selection, setSelection] = useState<Selection>({
    type: null,
    color: null,
    format: null,
  });
  const [isSelecting, setIsSelecting] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastMessageRef = useRef<HTMLDivElement>(null);

  // Calculate message height and adjust scroll with smooth animation
  const addMessageWithScroll = (newMessages: Message[]) => {
    // Store the current scroll position and scrollable area height
    const container = containerRef.current;
    if (!container) return setMessages(newMessages);

    const prevScrollTop = container.scrollTop;
    const prevScrollHeight = container.scrollHeight;

    // Update messages
    setMessages(newMessages);

    // After DOM update (using a timeout), calculate new scroll position
    setTimeout(() => {
      // Calculate how much the scroll height has increased
      const newScrollHeight = container.scrollHeight;
      const heightDifference = newScrollHeight - prevScrollHeight;

      // Adjust the scroll position with smooth animation
      container.scrollTo({
        top: prevScrollTop + heightDifference,
        behavior: 'smooth',
      });
    }, 10); // Slightly longer timeout to ensure DOM has updated
  };

  // Initialize the conversation with a delay
  useEffect(() => {
    const timer = setTimeout(() => {
      addMessageWithScroll([
        ...messages,
        {
          sender: 'ryanbot',
          content: conversation[0].prompt,
          isNew: true,
        },
      ]);

      // Show options after the message appears
      setTimeout(() => {
        setCurrentStep(0);
      }, 800);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Clear the "isNew" flag after animation completes
  useEffect(() => {
    const timer = setTimeout(() => {
      setMessages((prevMessages) =>
        prevMessages.map((msg) => ({
          ...msg,
          isNew: false,
        }))
      );
    }, 800);

    return () => clearTimeout(timer);
  }, [messages]);

  // Delayed options display when currentStep changes
  useEffect(() => {
    if (currentStep >= 0 && currentStep < conversation.length && !isSelecting) {
      // Hide options immediately
      setShowOptions(false);

      // Show options after delay
      const timer = setTimeout(() => {
        setShowOptions(true);
      }, 600); // Delay options by 600ms after question appears

      return () => clearTimeout(timer);
    } else {
      setShowOptions(false);
    }
  }, [currentStep, isSelecting]);

  const getLogoPath = () => {
    if (!selection.type || !selection.color || !selection.format) {
      return '';
    }

    // Fix the path format to ensure no double periods
    const format = selection.format.startsWith('.') ? selection.format.substring(1) : selection.format;

    // Use absolute URL for production, or relative for development
    const baseUrl =
      typeof window !== 'undefined' &&
      window.location.hostname !== 'localhost' &&
      window.location.hostname !== '127.0.0.1'
        ? 'https://column.com'
        : '';

    return `${baseUrl}/column-logo-files/column-${selection.type}-${selection.color}.${format}`;
  };

  const handleDownload = () => {
    const logoPath = getLogoPath();
    if (!logoPath) return;

    // Create an anchor element
    const downloadLink = document.createElement('a');

    // Set the href to the logo path (which now includes base URL if needed)
    downloadLink.href = logoPath;

    // Extract filename from path to use as download attribute
    const filename = logoPath.split('/').pop();

    // Set the download attribute to force download instead of navigation
    downloadLink.download = filename || 'column-logo';

    // Append to body, click and remove
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const handleOptionSelect = (option: string) => {
    // Hide selection options immediately
    setIsSelecting(true);
    setCurrentStep(-1);

    const step = conversation[currentStep];
    addMessageWithScroll([...messages, { sender: 'user' as const, content: option, isNew: true }]);

    // Delay Ryanbot response to feel more natural
    setTimeout(() => {
      // Special handling for "What??" option
      if (option === 'What??') {
        addMessageWithScroll([
          ...messages,
          { sender: 'user' as const, content: option, isNew: false },
          { sender: 'ryanbot' as const, content: step.response(option), isNew: true },
        ]);

        // Add Wordmark preview first
        setTimeout(() => {
          addMessageWithScroll([
            ...messages,
            { sender: 'user' as const, content: option, isNew: false },
            { sender: 'ryanbot' as const, content: step.response(option), isNew: false },
            {
              sender: 'ryanbot' as const,
              content: 'This is the wordmark which is our main/default logo and is probably what you are looking for.',
              isNew: true,
              showWordmark: true,
            },
          ]);

          // Add Symbol preview second
          setTimeout(() => {
            addMessageWithScroll([
              ...messages,
              { sender: 'user' as const, content: option, isNew: false },
              { sender: 'ryanbot' as const, content: step.response(option), isNew: false },
              {
                sender: 'ryanbot' as const,
                content:
                  'This is the wordmark which is our main/default logo and is probably what you are looking for.',
                isNew: false,
                showWordmark: true,
              },
              {
                sender: 'ryanbot' as const,
                content:
                  'This is the symbol which is used mostly when the full wordmark is too big, or otherwise when we feel like it!',
                isNew: true,
                showSymbol: true,
              },
            ]);

            // Go back to original question after showing previews
            setTimeout(() => {
              addMessageWithScroll([
                ...messages,
                { sender: 'user' as const, content: option, isNew: false },
                { sender: 'ryanbot' as const, content: step.response(option), isNew: false },
                {
                  sender: 'ryanbot' as const,
                  content:
                    'This is the wordmark which is our main/default logo and is probably what you are looking for.',
                  isNew: false,
                  showWordmark: true,
                },
                {
                  sender: 'ryanbot' as const,
                  content:
                    'This is the symbol which is used mostly when the full wordmark is too big, or otherwise when we feel like it!',
                  isNew: false,
                  showSymbol: true,
                },
                {
                  sender: 'ryanbot' as const,
                  content: 'So, which one do you need?',
                  isNew: true,
                },
              ]);

              // Show options again but without "What??"
              setTimeout(() => {
                setMessages((prevMessages) =>
                  prevMessages.map((msg) => ({
                    ...msg,
                    isNew: false,
                  }))
                );
                setCurrentStep(0);
                setIsSelecting(false);
                // Override options for step 0 to exclude "What??" afte which is been selected
                conversation[0].options = ['Wordmark', 'Symbol'];
              }, 600);
            }, 1000);
          }, 800);
        }, 800);
      } else {
        // Regular option handling (unchanged)
        addMessageWithScroll([
          ...messages,
          { sender: 'user' as const, content: option, isNew: false },
          { sender: 'ryanbot' as const, content: step.response(option), isNew: true },
        ]);

        setSelection((prev) => ({
          ...prev,
          [step.id]: option.toLowerCase(),
        }));

        if (currentStep < conversation.length - 1) {
          setTimeout(() => {
            addMessageWithScroll([
              ...messages,
              { sender: 'user' as const, content: option, isNew: false },
              { sender: 'ryanbot' as const, content: step.response(option), isNew: false },
              {
                sender: 'ryanbot' as const,
                content: conversation[currentStep + 1].prompt,
                isNew: true,
              },
            ]);

            // Re-enable selection and show next options
            setCurrentStep(currentStep + 1);
            setIsSelecting(false);
          }, 1500);
        } else {
          // Show download message after a longer delay
          setTimeout(() => {
            const downloadMessage: Message = {
              sender: 'ryanbot',
              content: 'Here ya go!',
              isDownload: true,
              isNew: true,
            };
            addMessageWithScroll([
              ...messages,
              { sender: 'user' as const, content: option, isNew: false },
              { sender: 'ryanbot' as const, content: step.response(option), isNew: false },
              downloadMessage,
            ]);
          }, 1500);
        }
      }
    }, 1000);
  };

  return (
    <MainContainer>
      <ChatContainer ref={containerRef}>
        <MessagesWrapper>
          <MessagesContainer>
            {messages.map((message, index) => {
              // Check if this message is a consecutive ryanbot message
              const isConsecutive =
                message.sender === 'ryanbot' && index > 0 && messages[index - 1].sender === 'ryanbot';

              return (
                <MessageRowWrapper
                  key={index}
                  isNew={message.isNew}
                  isConsecutive={isConsecutive}
                  ref={index === messages.length - 1 ? lastMessageRef : undefined}
                >
                  <MessageRow isUser={message.sender === 'user'}>
                    {message.sender === 'ryanbot' ? (
                      isConsecutive ? (
                        // Consecutive ryanbot message without avatar
                        <MessageContainer isLastMessage={index === messages.length - 1} isConsecutive>
                          <SuisseText size="300" color="gray-700">
                            {message.content}
                          </SuisseText>
                          {message.showSymbol && (
                            <ImagePreviewContainer>
                              <SymbolPreview
                                src="/column-logo-files/column-symbol-black.png"
                                alt="Symbol Preview"
                                onError={(e) => {
                                  console.error('Failed to load symbol image:', e);
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            </ImagePreviewContainer>
                          )}
                          {message.showWordmark && (
                            <ImagePreviewContainer>
                              <WordmarkPreview
                                src="/column-logo-files/column-logo-black.png"
                                alt="Wordmark Preview"
                                onError={(e) => {
                                  console.error('Failed to load wordmark image:', e);
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            </ImagePreviewContainer>
                          )}
                          {message.isDownload && (
                            <DownloadButtonWrapper>
                              <Button
                                onClick={handleDownload}
                                variant="primary"
                                style={{
                                  backgroundColor: '#222',
                                  color: '#fff',
                                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                                  border: '1px solid rgba(0, 0, 0, 0.05)',
                                  fontWeight: 500,
                                }}
                              >
                                Download
                              </Button>
                            </DownloadButtonWrapper>
                          )}
                        </MessageContainer>
                      ) : (
                        // First or standalone ryanbot message with avatar
                        <MessageWithAvatar>
                          <Avatar
                            src="/assets/ryanbot.jpg"
                            alt="Ryanbot Avatar"
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src =
                                'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="%23757575"><text x="50%" y="55%" dominant-baseline="middle" text-anchor="middle" font-size="12">R</text></svg>';
                            }}
                          />
                          <MessageContainer isLastMessage={index === messages.length - 1}>
                            <SuisseText size="300" color="gray-700">
                              {message.content}
                            </SuisseText>
                            {message.showSymbol && (
                              <ImagePreviewContainer>
                                <SymbolPreview
                                  src="/column-logo-files/column-symbol-black.png"
                                  alt="Symbol Preview"
                                  onError={(e) => {
                                    console.error('Failed to load symbol image:', e);
                                    e.currentTarget.style.display = 'none';
                                  }}
                                />
                              </ImagePreviewContainer>
                            )}
                            {message.showWordmark && (
                              <ImagePreviewContainer>
                                <WordmarkPreview
                                  src="/column-logo-files/column-logo-black.png"
                                  alt="Wordmark Preview"
                                  onError={(e) => {
                                    console.error('Failed to load wordmark image:', e);
                                    e.currentTarget.style.display = 'none';
                                  }}
                                />
                              </ImagePreviewContainer>
                            )}
                            {message.isDownload && (
                              <DownloadButtonWrapper>
                                <Button
                                  onClick={handleDownload}
                                  variant="primary"
                                  style={{
                                    backgroundColor: '#222',
                                    color: '#fff',
                                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                                    border: '1px solid rgba(0, 0, 0, 0.05)',
                                    fontWeight: 500,
                                  }}
                                >
                                  Download Logo
                                </Button>
                              </DownloadButtonWrapper>
                            )}
                          </MessageContainer>
                        </MessageWithAvatar>
                      )
                    ) : (
                      <MessageContainer isLastMessage={index === messages.length - 1} isUser>
                        <SuisseText size="300" color="blue-600">
                          {message.content}
                        </SuisseText>
                      </MessageContainer>
                    )}
                  </MessageRow>
                </MessageRowWrapper>
              );
            })}
          </MessagesContainer>
        </MessagesWrapper>
      </ChatContainer>

      <OptionsContainer>
        <OptionsButtonContainer isVisible={showOptions}>
          {currentStep >= 0 &&
            currentStep < conversation.length &&
            !isSelecting &&
            conversation[currentStep].options.map((option) => (
              <OptionButton key={option} onClick={() => handleOptionSelect(option)} variant="secondary">
                {option}
              </OptionButton>
            ))}
        </OptionsButtonContainer>
      </OptionsContainer>
    </MainContainer>
  );
};
